import React, { useEffect, useState } from "react";
import API from "../api";
import Layout from "../components/Layout";
import { FaEdit, FaTrash } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/style.css";
import "../css/expenses.css";

// ---------------- Helpers ----------------
const toInputDate = (d) => {
    if (!d) return "";
    const date = new Date(d);
    if (isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatMoney = (n) => {
    const num = Number(n || 0);
    return "₹ " + num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
};

const emptyForm = { date: "", category: "", amount: "", description: "" };

const Expenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [expenseType, setExpenseType] = useState("Direct");
    const [form, setForm] = useState(emptyForm);

    // FETCH ALL EXPENSES
    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const res = await API.get("/expenses");
            setExpenses(res.data || []);
        } catch (err) {
            console.log(err);
            toast.error("Failed to load expenses");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchExpenses();
    }, []);

    // OPEN ADD MODAL
    const openAdd = (type) => {
        setExpenseType(type);
        setEditId(null);
        setForm({ ...emptyForm, date: toInputDate(new Date()) });
        setShowModal(true);
    };

    // OPEN EDIT MODAL
    const openEdit = (exp) => {
        setExpenseType(exp.expenseType === "Direct" ? "Direct" : "Indirect");
        setEditId(exp._id);
        setForm({
            date: toInputDate(exp.date),
            category: exp.category || "",
            amount: exp.amount ?? "",
            description: exp.description || exp.particulars || ""
        });
        setShowModal(true);
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    // CREATE / UPDATE
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.date || !form.category || form.amount === "" || form.amount === null) {
            toast.error("Date, Expense Name / Particular and Amount are required");
            return;
        }

        const amount = Number(form.amount);
        if (!isFinite(amount) || amount < 0) {
            toast.error("Amount (₹) must be a valid numeric amount");
            return;
        }

        const payload = {
            date: form.date,
            category: form.category.trim(),
            particulars: form.description.trim(),
            description: form.description.trim(),
            amount,
            expenseType
        };

        try {
            if (editId) {
                await API.put(`/expenses/${editId}`, payload);
                toast.success("Expense updated");
            } else {
                await API.post("/expenses", payload);
                toast.success(expenseType === "Direct" ? "Direct expense added" : "Indirect expense added");
            }

            setShowModal(false);
            setEditId(null);
            setForm(emptyForm);
            fetchExpenses();
        } catch (err) {
            toast.error(err.response?.data?.message || "Something went wrong");
        }
    };

    // DELETE
    const handleDelete = async (exp) => {
        if (!window.confirm("Are you sure to delete this expense?")) return;

        try {
            await API.delete(`/expenses/${exp._id}`);
            toast.success("Expense deleted");
            fetchExpenses();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    // SPLIT INTO TWO COMPLETELY SEPARATE TABLES
    const directExpenses = expenses.filter((e) => String(e.expenseType || "") === "Direct");
    const indirectExpenses = expenses.filter((e) => String(e.expenseType || "") !== "Direct");

    const totalDirect = directExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
    const totalIndirect = indirectExpenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);

    // ---- Table renderer (used twice: Direct & Indirect) ----
    const renderExpenseTable = ({ items, total, addHandler, totalLabel, emptyText }) => (
        <div className="table-section expenses-section">
            <div className="expenses-section-head">
                <h3 className="expenses-title">{totalLabel.replace("Total ", "")}</h3>
                <button className="add-btn" onClick={addHandler}>
                    + {totalLabel.replace("Total ", "Add ")}
                </button>
            </div>

            <div className="table-responsive">
                <table className="expenses-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Particular / Expense</th>
                            <th className="num">Amount (₹)</th>
                            <th>Description</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((exp) => (
                            <tr key={exp._id}>
                                <td>{formatDate(exp.date)}</td>
                                <td>{exp.category}</td>
                                <td className="num">{formatMoney(exp.amount)}</td>
                                <td>{exp.description || exp.particulars || "-"}</td>
                                <td>
                                    <div className="actions">
                                        <button className="edit-btn" title="Edit" onClick={() => openEdit(exp)}><FaEdit /></button>
                                        <button className="delete-btn" title="Delete" onClick={() => handleDelete(exp)}><FaTrash /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr className="expenses-empty-row">
                                <td colSpan="5">{emptyText}</td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="expenses-total-row">
                            <td colSpan="2"><strong>{totalLabel}</strong></td>
                            <td className="num"><strong>{formatMoney(total)}</strong></td>
                            <td colSpan="2"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );

    return (
        <Layout>
            <ToastContainer position="top-right" autoClose={3000} />

            {/* ============================================================
                EXPENSES PAGE — SCOPING WRAPPER (.expenses-root)
                Every mobile-responsive rule in expenses.css is anchored to
                this wrapper, so the phone / tablet treatments below apply
                ONLY to the Expenses page. Shared classes reused by other
                pages (.topbar, .add-btn, .modal*) remain untouched there.
                ============================================================ */}
            <div className="expenses-root">

                {/* TOPBAR */}
                <div className="topbar">
                    <h2>Expenses</h2>
                </div>

                {loading && expenses.length === 0 && (
                    <div className="ui-loader">
                        <div className="ui-spinner"></div>
                        <span>Loading expenses…</span>
                    </div>
                )}

                <div className="expenses-page">
                    {/* ===== 1. DIRECT EXPENSES (SEPARATE TABLE) ===== */}
                    {renderExpenseTable({
                        items: directExpenses,
                        total: totalDirect,
                        totalLabel: "Total Direct Expenses",
                        emptyText: "No direct expenses recorded",
                        addHandler: () => openAdd("Direct")
                    })}

                    {/* ===== 2. INDIRECT EXPENSES (SEPARATE TABLE) ===== */}
                    {renderExpenseTable({
                        items: indirectExpenses,
                        total: totalIndirect,
                        totalLabel: "Total Indirect Expenses",
                        emptyText: "No indirect expenses recorded",
                        addHandler: () => openAdd("Indirect")
                    })}
                </div>

                {/* ===== EXPENSE FORM MODAL ===== */}
                {showModal && (
                    <div className="modal-overlay">
                        <div className="modal">
                            <div className="modal-header">
                                <h3>
                                    {editId
                                        ? (expenseType === "Direct" ? "Edit Direct Expense" : "Edit Indirect Expense")
                                        : (expenseType === "Direct" ? "Add Direct Expense" : "Add Indirect Expense")}
                                </h3>
                                <span onClick={() => setShowModal(false)}>×</span>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <label className="expenses-field-label">Date</label>
                                <input type="date" name="date" value={form.date} onChange={handleChange} required />

                                <label className="expenses-field-label">Expense Name / Particular</label>
                                <input
                                    type="text"
                                    name="category"
                                    placeholder="[ Enter expense name ]"
                                    value={form.category}
                                    onChange={handleChange}
                                    required
                                />

                                <label className="expenses-field-label">Amount (₹)</label>
                                <input
                                    type="number"
                                    name="amount"
                                    placeholder="[ Enter amount ]"
                                    step="0.01"
                                    min="0"
                                    value={form.amount}
                                    onChange={handleChange}
                                    required
                                />

                                <label className="expenses-field-label">Description</label>
                                <textarea
                                    name="description"
                                    placeholder="[ Enter description ]"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows="3"
                                />

                                <div className="modal-actions">
                                    <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="create-btn">
                                        {editId ? "Update" : "Add"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Expenses;